import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { AlertCircle } from 'lucide-react';
import PublicNavigation from '../../components/ui/PublicNavigation';
import { supabase } from '../../lib/supabase';
import { trackAuth, trackError } from '../../services/analyticsService';


const AdminLogin = () => {
  const navigate = useNavigate();
  
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Authenticate with Supabase
      const { data, error: authError } = await supabase?.auth?.signInWithPassword({
        email: email,
        password: password,
      });

      if (authError) {
        console.error('❌ Authentication error:', authError);
        trackError('auth_error', authError?.message, { user_type: 'admin' });
        
        // Provide user-friendly error messages
        if (authError?.message?.includes('Invalid login credentials')) {
          setError('Email ou mot de passe incorrect. Veuillez réessayer.');
        } else if (authError?.message?.includes('Email not confirmed')) {
          setError('Veuillez confirmer votre email avant de vous connecter.');
        } else {
          setError(authError?.message || 'Erreur de connexion. Veuillez réessayer.');
        }
        setLoading(false);
        return;
      }

      if (data?.user) {
        console.log('✅ Admin authenticated:', data?.user?.email);
        
        // Do NOT store auth in localStorage — Supabase manages the session
        // Clean up any legacy auth data
        localStorage.removeItem('adtAdminAuth');
        sessionStorage.removeItem('adtAdminAuth');

        // Track analytics BEFORE navigation (skip in sandboxed environments)
        try {
          // Check if we're in a sandboxed environment (Rocket.new preview)
          const isSandboxed = window.location?.hostname?.includes('builtwithrocket.new') || 
                              window.location?.hostname?.includes('rocket.new');
          
          if (!isSandboxed && typeof window.gtag !== 'undefined') {
            trackAuth('login', 'admin');
          }
        } catch (analyticsError) {
          console.warn('Analytics tracking skipped:', analyticsError);
        }

        // Navigate to correct admin dashboard
        navigate('/admin-shipments-dashboard', { replace: true });
      }
    } catch (err) {
      console.error('❌ Login error:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if already authenticated via Supabase session (not localStorage)
    const checkAuth = async () => {
      // Clean up any stale localStorage/sessionStorage auth data
      localStorage.removeItem('adtAdminAuth');
      sessionStorage.removeItem('adtAdminAuth');

      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session?.user) {
          navigate('/admin-shipments-dashboard', { replace: true });
        }
      } catch (err) {
        console.error('Session check error:', err);
      }
    };

    checkAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicNavigation />

      <div className="flex items-center justify-center px-3 sm:px-4 py-8 sm:py-12 lg:py-16">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Shield" size={24} className="text-green-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Connexion Admin</h1>
            <p className="text-sm sm:text-base text-gray-600">Accès réservé aux administrateurs</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm sm:text-base text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
            <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="Votre adresse email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 placeholder:text-gray-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 placeholder:text-gray-400"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-sm min-h-[44px] text-sm sm:text-base font-medium"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Connexion...</span>
                  </>
                ) : (
                  <>
                    <Icon name="LogIn" size={18} />
                    <span>Se connecter</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-4 sm:mt-6 text-center">
              <Link
                to="/password-reset"
                className="text-sm sm:text-base text-green-600 hover:text-green-700 active:text-green-800 font-medium inline-flex items-center space-x-1 min-h-[44px] py-2"
              >
                <span>Mot de passe oublié ?</span>
              </Link>
            </div>
          </div>

          {/* Back to Home */}
          <div className="mt-4 sm:mt-6 text-center">
            <Link
              to="/home-tracking"
              className="text-sm sm:text-base text-gray-600 hover:text-gray-900 font-medium inline-flex items-center space-x-2 min-h-[44px] py-2"
            >
              <Icon name="ArrowLeft" size={18} />
              <span>Retour à l'accueil</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;